import { StyleSheet } from 'react-native';

import { tokens } from '@design';
import { getTextAlign } from '../../utils/rtl';
// ============================================================
// Shared Chart Styles
// ============================================================

const getChartStyles = (isRTL: boolean) => StyleSheet.create({
  // Container styles
  container: {
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["4"],
    marginVertical: tokens.spacing.absolute["2"],
  },
  title: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },

  // Legend styles
  legendContainer: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
  },
  legendItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-5"],
  },
  legendSquare: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute["radius-2"],
  },
  legendLine: {
    width: 16,
    height: 3,
    borderRadius: tokens.radii.absolute["radius-2"],
  },
  legendLineDashed: {
    width: 16,
    height: 0,
    borderWidth: 1.5,
    borderStyle: 'dashed',
  },
  legendText: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
  },
  legendValue: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontWeight: '600',
  },

  // Tooltip styles
  tooltip: {
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
  },

  // Empty state styles
  emptyState: {
    height: 200,
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    borderRadius: tokens.radii.absolute["radius-12"],
  },
  emptyStateIcon: {
    marginBottom: tokens.spacing.absolute["2"],
  },
  emptyStateTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    textAlign: 'center',
  },
  emptyStateText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    textAlign: 'center',
  },

  // Chart wrapper styles
  chartWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartScrollContent: {
    flexGrow: 1,
    alignItems: 'center',
  },
});

// ============================================================
// Chart Color Palettes
// ============================================================

export const CHART_COLORS = {
  // Primary colors for charts
  primary: tokens.colors.semantic.general["Main Color"],
  secondary: tokens.colors.raw.blue["500"],
  tertiary: tokens.colors.raw.green["500"],
  quaternary: tokens.colors.raw.orange["500"],
  quinary: '#A855F7',
  senary: '#EC4899',

  // Status colors
  success: tokens.colors.raw.green["500"],
  warning: tokens.colors.raw.yellow["500"],
  danger: tokens.colors.raw.red["500"],
  info: tokens.colors.raw.blue["500"],

  // Achievement colors
  excellent: tokens.colors.raw.green["500"],
  good: '#84CC16',
  acceptable: tokens.colors.raw.yellow["500"],
  poor: tokens.colors.raw.red["500"],

  // Radar chart colors
  radar: {
    goals: tokens.colors.raw.green["500"],
    initiatives: tokens.colors.raw.orange["500"],
    processes: tokens.colors.raw.red["500"],
    services: '#A855F7',
    unitGoals: tokens.colors.raw.blue["500"],
    humanCapital: '#EC4899',
  },

  // Gauge colors
  gauge: {
    track: tokens.colors.raw.gray["200"],
    trackDark: tokens.colors.raw.gray["700"],
    actual: '#2CA02C',
    target: '#BBF7D0',
  },
};

// ============================================================
// Chart Threshold Helpers
// ============================================================

export const getStatusColor = (percentage: number): string => {
  if (percentage >= 100) return CHART_COLORS.excellent;
  if (percentage >= 80) return CHART_COLORS.good;
  if (percentage >= 60) return CHART_COLORS.acceptable;
  return CHART_COLORS.poor;
};

export const getAchievementStatus = (percentage: number): 'excellent' | 'good' | 'acceptable' | 'poor' => {
  if (percentage >= 100) return 'excellent';
  if (percentage >= 80) return 'good';
  if (percentage >= 60) return 'acceptable';
  return 'poor';
};

export const getStatusLabel = (status: 'excellent' | 'good' | 'acceptable' | 'poor'): string => {
  const labels = {
    excellent: 'ممتاز',
    good: 'جيد',
    acceptable: 'مقبول',
    poor: 'ضعيف',
  };
  return labels[status];
};

export const chartStyles = getChartStyles(true);
